import Loader from "@/components/loader";
import { Card } from "@heroui/card";
import { Button, Radio, RadioGroup } from "@heroui/react";
import { useEffect, useState } from "react";
import useBookingStore, { BookingMethod } from "@/store/booking-store";
import { useTranslations, useLocale } from "../../../../../i18n";
import { useStudioStore } from "@/store/studio-store";
import BookingOptionsContentTab from "./booking-options-content-tab";
import PaymentMethodsSelection from "./payment-methods";
import { useCheckoutUserPassesSubscriptions } from "@/hooks/use-checkout-user-passes-subscriptions";

// Helper function to get localized text
const getLocalizedText = (
  multilingualData: any,
  locale: string,
  fallback: string | null,
  defaultLocale: string = "en",
): string => {
  // If no multilingual data, return fallback
  if (!multilingualData || typeof multilingualData !== "object") {
    return fallback || "";
  }

  // Try to get text in current locale
  if (multilingualData[locale]) {
    return multilingualData[locale];
  }

  // Try default locale
  if (multilingualData[defaultLocale]) {
    return multilingualData[defaultLocale];
  }

  // Try first available language
  const availableLanguages = Object.keys(multilingualData);
  if (
    availableLanguages.length > 0 &&
    multilingualData[availableLanguages[0]]
  ) {
    return multilingualData[availableLanguages[0]];
  }

  // Final fallback
  return fallback || "";
};

function BookingOptions({
  hidePaymentMethods = false,
  autoSelectDefault = true,
  onSelect,
}: {
  hidePaymentMethods?: boolean;
  autoSelectDefault?: boolean;
  onSelect?: () => void;
}) {
  const studio = useStudioStore((state) => state.studio);
  const t = useTranslations("checkout");
  const locale = useLocale();

  const {
    selectedEvent,
    setSelectedPassId,
    setSelectedUserPass,
    setSelectedPass,
    setBookingMethod,
    selectedUserPass,
    selectedPass,
    bookingMethod,
  } = useBookingStore();

  const {
    passes: allPasses,
    subscriptions: allSubscriptions,
    isLoading,
  } = useCheckoutUserPassesSubscriptions();

  // Use the class session date so passes/memberships expiring before the
  // session date are excluded (not just passes expiring before today).
  const sessionDate = selectedEvent?.startAt
    ? new Date(selectedEvent.startAt)
    : new Date();

  const passes = allPasses.filter(
    (pass) => !pass.expiryDate || new Date(pass.expiryDate) >= sessionDate,
  );

  const subscriptions = allSubscriptions.filter(
    (sub: any) =>
      sub.status === "ACTIVE" &&
      (sub.creditsRemaining === null || sub.creditsRemaining > 0) &&
      (!sub.currentPeriodEnd || new Date(sub.currentPeriodEnd) >= sessionDate),
  );

  const [showBuyingOptions, setshowBuyingOptions] = useState(false);

  useEffect(() => {
    const defaultItem = selectedEvent?.pricing?.passes?.[0];
    // selectedEvent?.pricing?.subscriptions?.[0];

    if (
      autoSelectDefault &&
      defaultItem &&
      !selectedPass &&
      !selectedUserPass &&
      bookingMethod !== BookingMethod.USER_PASS
    ) {
      setSelectedPassId(defaultItem.id!);
      setSelectedPass(defaultItem);
      setBookingMethod("NEW_PASS");
    }
  }, [
    autoSelectDefault,
    selectedEvent,
    selectedPass,
    selectedUserPass,
    bookingMethod,
    setBookingMethod,
    setSelectedPass,
    setSelectedPassId,
  ]);

  /* useEffect(() => {
    console.log("passes or subscriptions changed:", passes);
    // Choose initial default values based on what's available
    if (passes?.length && passes?.length > 0) {
      setSelectedPassId(passes[0].id);
      setSelectedUserPass(passes[0]);
      setBookingMethod("USER_PASS");
    } /*  else if (subscriptions?.length && subscriptions?.length > 0) {
      setSelectedPassId(subscriptions[0].id);
      setSelectedUserPass(subscriptions[0]);
      setBookingMethod("USER_PASS");
    } * /
  }, [passes]); //  subscriptions */

  const handleUserPassSelection = (pass: any) => {
    setBookingMethod("USER_PASS");
    setSelectedPassId(pass.id);
    setSelectedUserPass(pass);
    setSelectedPass(null);
    onSelect?.();
  };

  // Helper function to get localized pass name
  const getLocalizedPassName = (pass: any): string => {
    if (!pass) return "";
    if (typeof pass.title === "string") return pass.title;
    if (typeof pass.passType?.title === "string") return pass.passType.title;
    if (typeof pass.any?.title === "string") return pass.any.title;
    return getLocalizedText(
      pass.passType?.title ?? pass.any?.title ?? pass.title,
      locale,
      pass.passType?.name ?? pass.any?.name ?? pass.name,
      studio?.defaultLocale || "en",
    );
  };

  const shouldShowPaymentMethodsSelection = () => {
    if (bookingMethod === BookingMethod.USER_PASS && selectedUserPass)
      return false;

    if (
      bookingMethod === BookingMethod.NEW_PASS &&
      selectedPass &&
      selectedPass.price > 0
    ) {
      return true;
    }

    return false;
  };

  const shouldShowBuyingOptions = () => {
    if (showBuyingOptions) return true;

    if ([...passes, ...subscriptions].length > 0) return false;

    return true;
  };

  const availablePasses = selectedEvent?.pricing?.passes ?? [];
  // const availableSubscriptions = selectedEvent?.pricing?.subscriptions ?? [];

  return (
    <>
      {[...passes, ...subscriptions].length > 0 && (
        <Card className="p-3 md:p-6 mb-6 " shadow="sm">
          <h2 className="text-lg text-gray-500 font-semibold mb-4">
            {t("howDoYouWantToParticipate") ||
              "How do you want to participate?"}
          </h2>

          {isLoading ? (
            <Loader />
          ) : (
            <div className="mb-6">
              {[...passes, ...subscriptions].length > 0 && (
                <h3 className="text-lg font-medium mb-4">
                  {t("yourPass") || "Your Pass"}
                </h3>
              )}
              <RadioGroup
                value={selectedUserPass?.id}
                onValueChange={(id) => {
                  const pass = [...passes, ...subscriptions].find(
                    (item) => item.id === id,
                  );
                  if (pass) {
                    handleUserPassSelection(pass);
                    return;
                  }
                }}
                className="mb-4"
              >
                {[...passes, ...subscriptions].map((pass) => (
                  <div
                    key={pass.id}
                    className="flex justify-between items-center p-3 md:p-4 border rounded-lg mb-2 cursor-pointer"
                    onClick={() => {
                      handleUserPassSelection(pass);
                    }}
                  >
                    <Radio value={pass.id}>
                      <div className="flex flex-col md:flex-row md:items-center md:gap-4">
                        <span className="font-medium">
                          {getLocalizedPassName(pass)}
                        </span>
                      </div>
                    </Radio>
                    <span className="text-sm md:text-base font-medium whitespace-nowrap">
                      {pass.creditsInitial === null
                        ? t("unlimited") || "Unlimited"
                        : `${pass.creditsRemaining} ${t("classesRemaining") || "classes remaining"}`}
                    </span>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {!showBuyingOptions && (
            <Button
              variant="light"
              onPress={() => setshowBuyingOptions(true)}
              className="mt-4"
            >
              {t("needMoreCredits")}
            </Button>
          )}
        </Card>
      )}

      {/* Purchase New Options Section */}
      {shouldShowBuyingOptions() && (
        <Card className="p-3 md:p-6" shadow="sm">
          {isLoading ? (
            <Loader />
          ) : (
            selectedEvent && (
              <div>
                <h2 className="text-lg text-gray-500 font-semibold mb-4">
                  {t("purchaseOptions") || "Purchase options"}
                </h2>
                {availablePasses.length === 0 ? (
                  /* availableSubscriptions.length === 0 */ <div className="text-gray-500">
                    {t("noBookingOptions") ||
                      "No booking options available for this class."}
                  </div>
                ) : (
                  <>
                    {availablePasses.length > 0 && (
                      <div key="passes">
                        {/* TODO add the title once the subscriptions are ready */}
                        {/* <h3 className="text-lg font-medium mb-4">
                          {t("passes") || "Passes"}
                        </h3> */}
                        <div className="flex flex-col gap-4">
                          <BookingOptionsContentTab
                            items={availablePasses}
                            onSelect={onSelect}
                          />
                        </div>
                      </div>
                    )}
                    {/* {availableSubscriptions.length > 0 && (
                      <div>
                        <h3 className="text-lg font-medium mb-4">
                          {t("subscriptions") || "Subscriptions"}
                        </h3>
                        <div className="flex flex-col gap-4">
                          <BookingOptionsContentTab
                            items={availableSubscriptions}
                          />
                        </div>
                      </div>
                    )} */}
                  </>
                )}
              </div>
            )
          )}
        </Card>
      )}

      {!hidePaymentMethods && shouldShowPaymentMethodsSelection() && (
        <PaymentMethodsSelection />
      )}
    </>
  );
}

export default BookingOptions;
